import type { PlanActivation, TaskPlan } from "./types";

/**
 * Everything the Task plans tab derives from its own read plus the shared filters.
 *
 * The plans tab's whole subject is the release decision: which waves are queued,
 * what each would put on the floor, and whether it can be pulled back. None of
 * that is a field, and none of it needs the plan's tasks either -
 * `TaskPlanActivationState` rolls up the counts a card reports, which is what
 * lets a collapsed card be drawn from the plan listing alone and the tasks be
 * fetched only when someone opens one.
 *
 * It knows nothing about the All tasks tab, and shares only what genuinely is
 * shared - the filter types, and the row shape in `task-model`.
 *
 * It narrows nothing, and it is handed no filters to narrow by. Every filter
 * this tab offers is a query param (see `use-plans.ts`), so the plans handed to
 * `buildPlansModel` are the answer and this file's whole job is to say what each
 * one is.
 */

/**
 * A plan's status as the card states it.
 *
 * Five words, because the release decision has five answers. A plan whose
 * activation job is running is not "not activated" - offering an Activate button
 * on it is how you get a duplicate activation and a 409 - and one whose
 * activation failed is not "activated", though it does need the button back.
 */
export type PlanStatusKey =
  | "notActivated"
  | "activationFailed"
  | "activating"
  | "activated"
  | "completed";

/** A plan as one card, drawn without reading a single task. */
export interface PlanRow {
  plan: TaskPlan;
  statusKey: PlanStatusKey;
  /** Tasks the card claims: what is on the floor, or what would be released. */
  taskCount: number;
  /** Tasks finished, and the total they are finished out of. */
  tasksDone: number;
  tasksTotal: number;
  percent: number;
  /** A selector has committed to some of it, which is what locks a deactivation. */
  isStarted: boolean;
  canActivate: boolean;
  canDeactivate: boolean;
}

export interface PlansModel {
  planRows: PlanRow[];
}

/**
 * What the card's status tag says, as one exhaustive read of the union.
 *
 * Five words for four cases, because a released plan says two different things
 * depending on its tasks: being worked, or done. That last one is a fact about
 * *execution* rather than about the activation, and it is read off the
 * activation's own `execution_completed_at` stamp rather than derived from the
 * counts - which is the same field `fulfillmentState=completed` filters on (see
 * `activationQuery` in `use-plans.ts`). Deriving it here instead would be a
 * second definition of "done", and the chip that selected the card and the tag on
 * it would eventually answer differently for the same plan.
 */
function statusOf(activation: PlanActivation): PlanStatusKey {
  switch (activation.kind) {
    case "never":
      return "notActivated";
    case "failed":
      return "activationFailed";
    case "in-flight":
      return "activating";
    case "released":
      return activation.executionCompletedAt !== null
        ? "completed"
        : "activated";
  }
}

function progressOf(plan: TaskPlan): {
  taskCount: number;
  tasksDone: number;
  tasksTotal: number;
} {
  const { activation } = plan;

  if (activation.kind !== "released") {
    // Nothing is on the floor, so the only honest count is what releasing would
    // put there - which is the number the Activate button's note quotes.
    return {
      taskCount: plan.plannedTaskCount,
      tasksDone: 0,
      tasksTotal: plan.plannedTaskCount,
    };
  }

  return {
    taskCount: activation.totalTasks,
    tasksDone: activation.counts.completed,
    tasksTotal: activation.totalTasks,
  };
}

export function buildPlansModel(plans: ReadonlyArray<TaskPlan>): PlansModel {
  const planRows = plans.map((plan): PlanRow => {
    const statusKey = statusOf(plan.activation);
    const { taskCount, tasksDone, tasksTotal } = progressOf(plan);

    const released = plan.activation.kind === "released";
    /*
      The stamp *or* the counts, which is the one place the two are not held to
      each other. `execution_started_at` is null for an activation minted before
      the column existed, and there the counts are the only evidence there is -
      reading the stamp alone would offer a Deactivate button on a plan selectors
      are already picking. Widening what counts as started is safe in a way that
      narrowing it is not, so this errs at the button rather than at the tag.
    */
    const isStarted =
      plan.activation.kind === "released" &&
      (plan.activation.executionStartedAt !== null ||
        plan.activation.counts.claimed + plan.activation.counts.completed > 0);

    return {
      plan,
      statusKey,
      taskCount,
      tasksDone,
      tasksTotal,
      percent:
        tasksTotal === 0 ? 0 : Math.round((tasksDone / tasksTotal) * 100),
      isStarted,
      // A plan mid-activation has a write already in flight, and a finished
      // one has nothing left to release.
      canActivate:
        plan.activation.kind === "never" || plan.activation.kind === "failed",
      // A started plan cannot be pulled back out from under the selectors on
      // it, and one still settling is the exact case the service rejects.
      canDeactivate:
        released &&
        statusKey !== "completed" &&
        !isStarted &&
        !(plan.activation.kind === "released" && plan.activation.isSettling),
    };
  });

  return { planRows };
}
